import React, { useMemo, useState } from "react"
import { useWatch } from "react-hook-form"

import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Combobox } from "shared/ui/input/combobox/Combobox"

import { RJSFFormattedSelectOption } from "./components/ValueDropdownSingleValue"
import { RJSFFormFieldProps } from "./types"

interface RJSFStringComboboxProps
  extends Pick<RJSFFormFieldProps, "path" | "schema" | "isDisabled"> {
  creatable?: boolean
}

export const RJSFStringCombobox = React.memo(
  ({ path, schema, isDisabled, creatable }: RJSFStringComboboxProps) => {
    const label = `${schema.title}${schema.unit ? ` (${schema.unit})` : ""}`
    const value = useWatch({ name: path })

    const [inputValue, setInputValue] = useState("")

    const items = useMemo(() => {
      if (!creatable) return schema.options
      const base = schema.options ?? []
      const extras: { label: string; value: string }[] = []

      if (value && !base.some(opt => opt.value === value)) {
        extras.push({ label: value, value })
      }

      if (
        inputValue &&
        !base.some(opt => opt.label === inputValue) &&
        inputValue !== value
      ) {
        extras.push({ label: inputValue, value: inputValue })
      }

      return extras.length ? [...base, ...extras] : schema.options
    }, [value, creatable, inputValue, schema.options])

    const selectedOption = useMemo(() => {
      return items?.find(option => option.value === value)
    }, [items, value])

    return (
      <Combobox.Root
        name={path}
        label={label}
        className={schema.meta?.displayType}
        disabled={isDisabled}
        creatable={creatable}
        items={items}
      >
        <Combobox.Label />
        <Combobox.Readonly showAlways>
          <RJSFFormattedSelectOption option={selectedOption} />
        </Combobox.Readonly>
        <Combobox.Control onInputValueChange={setInputValue}>
          <Combobox.Trigger>
            <Combobox.Value>
              <RJSFFormattedSelectOption option={selectedOption} />
            </Combobox.Value>
            <Combobox.Icon />
          </Combobox.Trigger>
          <Combobox.Portal>
            <Combobox.Positioner sideOffset={8}>
              <Combobox.Popup aria-label={label}>
                <Combobox.InputContainer>
                  <Combobox.Input />
                  <Icon name={IconName.SEARCH} />
                </Combobox.InputContainer>
                <Combobox.Empty />
                <Combobox.List>
                  {option => (
                    <Combobox.Item key={option.value} value={option}>
                      <Combobox.ItemIndicator />
                      <Combobox.ItemContent>
                        <RJSFFormattedSelectOption option={option} />
                      </Combobox.ItemContent>
                    </Combobox.Item>
                  )}
                </Combobox.List>
              </Combobox.Popup>
            </Combobox.Positioner>
          </Combobox.Portal>
        </Combobox.Control>
        <Combobox.Readonly items={schema.options ?? undefined} />
        <Combobox.ErrorMessage>{schema.error}</Combobox.ErrorMessage>
      </Combobox.Root>
    )
  }
)
